import React from 'react';
import { Card, Badge } from 'react-bootstrap';
import { Eye, Users, Lock, Tag } from 'lucide-react';

const PromptPreview = ({ prompt }) => {
  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public':
        return <Eye size={16} className="me-1" />;
      case 'team':
        return <Users size={16} className="me-1" />;
      case 'private':
        return <Lock size={16} className="me-1" />;
      default:
        return <Lock size={16} className="me-1" />;
    }
  };

  const getVisibilityColor = (visibility) => {
    switch (visibility) {
      case 'public':
        return 'success';
      case 'team':
        return 'info';
      case 'private':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-transparent">
        <h6 className="mb-0">Preview</h6>
      </Card.Header>
      <Card.Body>
        {prompt.title ? (
          <>
            <h5 className="mb-2">{prompt.title}</h5>
            
            {prompt.description && (
              <p className="text-muted small mb-3">{prompt.description}</p>
            )}

            <div className="mb-3">
              <Badge bg={getVisibilityColor(prompt.visibility)} className="me-2">
                {getVisibilityIcon(prompt.visibility)}
                {prompt.visibility}
              </Badge>
              
              {prompt.category && (
                <Badge bg="outline-secondary" className="me-2">
                  {prompt.category}
                </Badge>
              )}
              
              {prompt.isTemplate && (
                <Badge bg="warning" text="dark">
                  Template
                </Badge>
              )}
            </div>

            {prompt.aiModels && prompt.aiModels.length > 0 && (
              <div className="mb-3">
                <small className="text-muted d-block mb-1">Compatible with:</small>
                <div className="d-flex flex-wrap gap-1">
                  {prompt.aiModels.map(model => (
                    <Badge key={model} bg="primary" className="small">
                      {model}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {prompt.tags && prompt.tags.length > 0 && (
              <div className="mb-3">
                <small className="text-muted d-block mb-1">Tags:</small>
                <div className="d-flex flex-wrap gap-1">
                  {prompt.tags.map(tag => (
                    <Badge key={tag.id || tag} bg="outline-secondary" className="small">
                      <Tag size={12} className="me-1" />
                      {tag.displayName || tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="border rounded p-3 bg-light">
              <small className="text-muted d-block mb-2">Prompt Content:</small>
              <pre className="mb-0 text-wrap" style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                {prompt.content}
              </pre>
            </div>

            {prompt.isTemplate && prompt.templateVariables && prompt.templateVariables.length > 0 && (
              <div className="mt-3">
                <small className="text-muted d-block mb-1">Template Variables:</small>
                <div className="d-flex flex-wrap gap-1">
                  {prompt.templateVariables.map(variable => (
                    <Badge key={variable.name} bg="info" className="small">
                      {variable.name}
                      {variable.required && <span className="ms-1">*</span>}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center text-muted py-4">
            <p className="mb-0">Start typing to see a preview of your prompt</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default PromptPreview;
